import React, { CSSProperties, ChangeEvent, FocusEvent, KeyboardEvent, ReactNode, useRef, useState } from "react";

import { Input as AntInput } from "antd";
import { RuleObject } from "antd/es/form";
import { NamePath } from "antd/es/form/interface";

import { parseInputRules } from "utils";

import { Form, InputLabel, Typography } from "components";

export type InputProps = {
  dataTestId?: string;
  allowClear?: boolean;
  className?: string;
  labelClassName?: string;
  disabled?: boolean;
  name?: NamePath;
  initialValue?: number | string;
  label: string;
  tooltip?: string;
  maxLength?: number;
  message?: string;
  prefix?: ReactNode;
  rules?: RuleObject[];
  style?: CSSProperties;
  suffix?: ReactNode;
  value?: number | string;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  validateTrigger?: string;
  validateFirst?: boolean;
  guidanceText?: string;
  onBlur?: (e: FocusEvent) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent) => void;
  onPressEnter?: (e: KeyboardEvent) => void;
};

export const Input = ({
  dataTestId = "",
  allowClear,
  className = "",
  labelClassName = "",
  disabled,
  name,
  initialValue,
  label = "",
  tooltip,
  maxLength,
  message,
  prefix,
  rules = [],
  style,
  suffix,
  value,
  placeholder,
  autoComplete,
  inputMode,
  validateTrigger,
  validateFirst,
  guidanceText,
  onBlur,
  onFocus,
  onChange,
  onPressEnter,
}: InputProps) => {
  const inputElement = useRef(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(initialValue);

  const handleBlur = (e: FocusEvent) => {
    setHasBeenFocused(false);
    onBlur?.(e);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInternalValue((e.target as HTMLInputElement).value);
    onChange?.(e);
  };

  const handleFocus = (e: FocusEvent) => {
    setHasBeenFocused(true);
    onFocus?.(e);
  };

  const handleLabelClick = () => {
    setHasBeenFocused(true);
    (inputElement.current! as HTMLInputElement)?.focus();
  };

  const active = !!(hasBeenFocused || initialValue || internalValue || internalValue === 0);
  const required = !!(rules && rules[0] && rules[0].required);

  return (
    <div className={className}>
      <Form.Item
        className={`floating-input ${hasBeenFocused ? "active" : ""} ${disabled ? "disabled" : "message"} ${className} ${prefix ? "prefix" : ""}`}
        colon={false}
        label={<InputLabel active={active} required={required} label={label} className={labelClassName} handleClick={handleLabelClick} tooltip={tooltip} />}
        name={name}
        rules={parseInputRules(rules, message)}
        validateFirst={validateFirst ?? false}
        initialValue={initialValue || undefined}
        style={style}
        validateTrigger={validateTrigger}
      >
        <AntInput
          data-testid={dataTestId}
          allowClear={allowClear}
          maxLength={maxLength}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
          prefix={prefix}
          ref={inputElement}
          suffix={suffix}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          onPressEnter={onPressEnter}
          disabled={disabled}
          value={value}
        />
      </Form.Item>
      {guidanceText ? (
        <Typography variant="paragraph-s" color="grey-600" className="margin-top-50">
          {guidanceText}
        </Typography>
      ) : null}
    </div>
  );
};
